import { z } from 'zod'
import { ackEnvelope, assertAck, flatCountEnvelope } from './envelope'
import type { ApiClient } from './client'

/**
 * Integrations (plugins) — POST /{clusterId}/api/plugins/*
 *
 * The platform's integration store (docs/IA.md IA3). Contract read off the
 * legacy service and screen (`services/integrations_apis.js`,
 * `pages/admin/integrations`), with `list` and `list_multi` confirmed by the
 * live navigation sweep (docs/inventory/api-observed.json,
 * `gotham/system/integrations`).
 *
 * Facts this design leans on, several of which are traps:
 *
 * - The ENVELOPE IS NOT the mgmt one. Here `data` is a BARE ARRAY
 *   (`{ status, count, data: [...] }`), not `{ data: { result: [...] } }`.
 *   `count` is still the total across all pages. Reusing `resultEnvelope`
 *   here parses to nothing.
 *
 * - The legacy service file holds TWO generations of methods: an older set
 *   with the scope in the path (`/api/plugins/{scope}/list`) and a newer flat
 *   set. Only the newer set is exercised by the running screen, and only the
 *   newer set appeared on the live sweep. This binds the newer set.
 *
 * - DELETE IS `delete_multi_config`, not `delete_plugin`. `delete_plugin`
 *   exists in the service, is old-generation, and the screen never calls it.
 *   It deletes a CONFIG, not a plugin, and the screen only offers it for
 *   `allow_multi` plugins that actually have a `config_id`.
 *
 * - `validated` is an OBJECT, `{ validated: boolean, message: string }`. The
 *   legacy page is inconsistent about it — its list handler flattens it to
 *   the message while its table reads the object — so the boolean is what
 *   this models. Never string-match the message.
 *
 * - `list_multi` is a CAPABILITY LOOKUP, not a second list: it returns which
 *   plugin names may hold more than one config. Those rows are titled by
 *   `config_name` rather than `display_name`, so omitting it titles exactly
 *   the multi-config rows wrongly.
 *
 * - The manifest is keyed by `plugin_name`; a saved config by `id` +
 *   `config_id`. An UNCONFIGURED row with `config_id === null` has no config
 *   to read at all.
 */

/** Whether a plugin's configuration passed the backend's own validation. */
export const pluginValidation = z
  .object({
    validated: z.boolean().default(false),
    message: z.string().default(''),
  })
  .passthrough()

export const plugin = z
  .object({
    /** The plugin's id. Not unique per ROW — a multi-config plugin repeats it. */
    id: z.string(),
    /** The configuration's id. Null on a plugin that has never been configured. */
    config_id: z.string().nullable().default(null),
    /** Machine name; the key the manifest is fetched by. */
    plugin_name: z.string().default(''),
    /** Human name of the PLUGIN. */
    display_name: z.string().default(''),
    /** Human name of this CONFIG. Only meaningful when allow_multi. */
    config_name: z.string().nullable().default(null),
    /** Bare `tag.map()` in the legacy renderer, so this must never be undefined. */
    tag: z.array(z.string()).default([]),
    created_by: z.string().default(''),
    validated: pluginValidation.default({ validated: false, message: '' }),
    /** 'CONFIGURED' | 'UNCONFIGURED'. Permissive: an added value must not blank the page. */
    config_status: z.string().default('UNCONFIGURED'),
    /** 'ACTIVE' | 'INACTIVE'. */
    status: z.string().default('INACTIVE'),
    /** Whether this plugin may hold more than one configuration. */
    allow_multi: z.boolean().default(false),
    scope_id: z.string().default(''),
  })
  .passthrough()

export type Plugin = z.output<typeof plugin>
export type PluginValidation = z.output<typeof pluginValidation>

/**
 * The plugins envelope: `data` is the array itself.
 *
 * Deliberately NOT `resultEnvelope` — see the header. The difference is
 * silent: `data.result` on this payload is `undefined`, which a permissive
 * parse would render as an empty page rather than an error.
 */
export const pluginsResponse = flatCountEnvelope(plugin)

/** `list_multi` returns the plugins that may hold several configs. */
export const multiConfigResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(z.object({ plugin_name: z.string() }).passthrough()).default([]),
})

export const CONFIGURED = 'CONFIGURED'
export const UNCONFIGURED = 'UNCONFIGURED'
export const ACTIVE = 'ACTIVE'
export const INACTIVE = 'INACTIVE'

export interface PluginFilter {
  filter_key: string
  filter_condition: 'AND' | 'OR'
  filter_value: unknown
  filter_operand: 'LIKE' | '=' | 'IN'
}

export interface PluginsPage {
  plugins: Plugin[]
  /** Total across all pages, from `count`. */
  total: number
}

export interface PluginsQuery {
  clusterId: string
  scopeId: string
  /** 1-based. */
  pageNo?: number
  pageSize?: number
  /** Substring match. Note the legacy screen filters on `config_name`. */
  search?: string
  /** Facet filters, e.g. created_by / tag as IN lists. */
  filters?: PluginFilter[]
}

export interface PluginStatusInput {
  clusterId: string
  scopeId: string
  id: string
  configId: string | null
  status: string
}

export interface PluginConfigDeleteInput {
  clusterId: string
  scopeId: string
  id: string
  configId: string
}

export interface PluginManifestQuery {
  clusterId: string
  scopeId: string
  /** The MACHINE name — the manifest is keyed by plugin_name, not by id. */
  pluginName: string
}

export interface PluginConfigQuery {
  clusterId: string
  scopeId: string
  id: string
  configId: string
}

export interface PluginConfigSaveInput {
  clusterId: string
  scopeId: string
  id: string
  /** Null when saving the FIRST configuration of a plugin. */
  configId: string | null
  data: ConfigValues
  /** Present only when the manifest has a file field and one was chosen. */
  file?: File | null
}

export interface PluginsApi {
  list: (q: PluginsQuery) => Promise<PluginsPage>
  /** Plugin names that may hold more than one configuration. */
  multiConfigNames: (q: { clusterId: string; scopeId: string }) => Promise<string[]>
  setStatus: (input: PluginStatusInput) => Promise<void>
  deleteConfig: (input: PluginConfigDeleteInput) => Promise<void>
  /** The plugin's own description of its config form. */
  manifest: (q: PluginManifestQuery) => Promise<ManifestField[]>
  /** The values last saved for one configuration. */
  config: (q: PluginConfigQuery) => Promise<ConfigValues>
  saveConfig: (input: PluginConfigSaveInput) => Promise<void>
}

/**
 * The search filter. `config_name`, not `display_name` — the legacy screen
 * searches the name of the CONFIG, which is what distinguishes two rows of the
 * same plugin.
 */
export function configNameFilter(search: string): PluginFilter[] {
  const value = search.trim()
  if (!value) return []
  return [{ filter_key: 'config_name', filter_condition: 'AND', filter_value: value, filter_operand: 'LIKE' }]
}

/** A facet over a set of values, e.g. `created_by` or `tag`. */
export function inFilter(key: string, values: string[]): PluginFilter[] {
  if (!values.length) return []
  return [{ filter_key: key, filter_condition: 'AND', filter_value: values, filter_operand: 'IN' }]
}

const ackSchema = ackEnvelope

export function createPluginsApi(client: ApiClient): PluginsApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 50, search = '', filters = [] }) => {
      const addfilters = [...filters, ...configNameFilter(search)]
      const res = await client.request('POST', `/${clusterId}/api/plugins/list`, {
        body: {
          scope_id: scopeId,
          pageno: pageNo,
          pagesize: pageSize,
          // The legacy screen omits the key entirely when there is nothing to
          // filter on; an empty array is a different, unverified request.
          ...(addfilters.length ? { addfilters } : {}),
        },
        schema: pluginsResponse,
      })
      return { plugins: res.data, total: res.count }
    },

    multiConfigNames: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/api/plugins/list_multi`, {
        body: { scope_id: scopeId },
        schema: multiConfigResponse,
      })
      return res.data.map((p) => p.plugin_name)
    },

    setStatus: async ({ clusterId, scopeId, id, configId, status }) => {
      const res = await client.request('POST', `/${clusterId}/api/plugins/change_status`, {
        body: { id, config_id: configId, scope_id: scopeId, status },
        schema: ackSchema,
      })
      assertAck(res, 'Changing the status')
    },

    deleteConfig: async ({ clusterId, scopeId, id, configId }) => {
      // delete_multi_config, NOT delete_plugin. See the header.
      const res = await client.request('POST', `/${clusterId}/api/plugins/delete_multi_config`, {
        body: { scope_id: scopeId, id, config_id: configId },
        schema: ackSchema,
      })
      assertAck(res, 'Deleting the configuration')
    },

    manifest: async ({ clusterId, scopeId, pluginName }) => {
      const res = await client.request('POST', `/${clusterId}/api/plugins/configure`, {
        // `name`, and it is the machine name. An id here returns nothing.
        body: { name: pluginName, scope_id: scopeId },
        schema: manifestResponse,
      })
      return res.data
    },

    config: async ({ clusterId, scopeId, id, configId }) => {
      const res = await client.request('POST', `/${clusterId}/api/plugins/get_config`, {
        body: { config_id: configId, id, scope_id: scopeId },
        schema: configResponse,
      })
      return res.data
    },

    saveConfig: async ({ clusterId, scopeId, id, configId, data, file }) => {
      const payload = { data, id, scope_id: scopeId, config_id: configId }

      if (file) {
        // The multipart shape, reproduced exactly: the file, the same payload
        // JSON-stringified under `data`, and scope_id AGAIN at top level. The
        // duplication looks redundant; the backend is assumed to read one of
        // them and it is not known which.
        //
        // Deliberate divergence: the legacy screen forks on the literal field
        // name 'PRIVATE_KEY'. This forks on there BEING a file, so a manifest
        // that names its file field anything else still works — under legacy
        // it would silently take the JSON path and lose the upload.
        const form = new FormData()
        form.append('file', file)
        form.append('data', JSON.stringify(payload))
        form.append('scope_id', scopeId)
        const res = await client.request('POST', `/${clusterId}/api/plugins/save_config`, {
          body: form,
          schema: ackSchema,
        })
        assertAck(res, 'Saving the configuration')
        return
      }

      const res = await client.request('POST', `/${clusterId}/api/plugins/save_config`, {
        body: payload,
        schema: ackSchema,
      })
      assertAck(res, 'Saving the configuration')
    },
  }
}

/* ------------------------------------------------------------------ display */

/** The name this row should be titled by (`list_multi` decides). */
export function pluginTitle(p: Plugin, multiNames: readonly string[]): string {
  return multiNames.includes(p.plugin_name) && p.config_name ? p.config_name : p.display_name
}

export function isConfigured(p: Plugin): boolean {
  return p.config_status === CONFIGURED
}

export function isActive(p: Plugin): boolean {
  return p.status === ACTIVE
}

/**
 * Whether the ACTIVE/INACTIVE switch may be moved.
 *
 * Asymmetric, and deliberately so — this reproduces the legacy rule rather
 * than tidying it. Turning an integration ON requires a configured AND
 * validated plugin. Turning one OFF also accepts one that is merely already
 * active: an integration that went invalid while running must still be
 * switchable off, or it cannot be stopped from the console at all.
 */
export function canToggleStatus(p: Plugin): boolean {
  if (!isConfigured(p)) return false
  return p.validated.validated || isActive(p)
}

/** Deleting a CONFIG, not a plugin: only multi-config rows that have one. */
export function canDeleteConfig(p: Plugin): boolean {
  return p.allow_multi && p.config_id !== null
}

/* --------------------------------------------------------------- manifests */

/**
 * One field of a plugin's configuration manifest, from
 * POST /{clusterId}/api/plugins/configure.
 *
 * The manifest is the plugin's own description of its config form: the console
 * knows nothing about any particular integration, it just renders this.
 *
 * `DefaultDisplay` comes from the manifest and means "show without expanding
 * advanced settings". The legacy form ALSO mutates it in place when a select
 * carrying `fieldMap` changes — see `fieldMap` below.
 */
export const manifestField = z
  .object({
    fieldName: z.string(),
    displayName: z.string().default(''),
    /** 'Basic' | anything else. Only consulted in basic mode. */
    fieldType: z.string().default(''),
    /** One of MANIFEST_INPUT_TYPES; kept permissive so an added type renders as text. */
    input_type: z.string().default('input'),
    default: z.unknown().optional(),
    required: z.boolean().default(false),
    /**
     * A credential. Rendered masked, and — the part that matters — read back
     * from the server as SENSITIVE_MASK rather than as its value.
     */
    Sensitive: z.boolean().default(false),
    helperText: z.string().default(''),
    placeholder: z.string().default(''),
    /** Options for select / multi select. */
    input_options: z.array(z.string()).default([]),
    /**
     * Maps a select OPTION value to the fieldName it reveals. The legacy form
     * uses it to switch between "paste a key" and "upload a key file".
     */
    fieldMap: z.record(z.string()).optional(),
    /** Show this field only while another field holds one of these values. */
    visible_when: z
      .object({
        field: z.string(),
        value: z.unknown().optional(),
        values: z.array(z.unknown()).optional(),
      })
      .optional(),
    /** Visible without expanding advanced settings. */
    DefaultDisplay: z.boolean().default(false),
  })
  .passthrough()

export type ManifestField = z.output<typeof manifestField>

export const manifestResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(manifestField).default([]),
})

/** Saved values for one configuration. Shapes are per input type. */
export const configResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.record(z.unknown()).default({}),
})

export type ConfigValues = Record<string, unknown>

/**
 * What the server returns in place of a stored credential.
 *
 * Sending it back would store the mask AS the credential, and the integration
 * would then fail to authenticate with nothing on screen to explain it. A
 * named constant so that a backend which switches to another mask breaks a
 * test rather than silently storing '••••'.
 */
export const SENSITIVE_MASK = '***'

export const MANIFEST_INPUT_TYPES = [
  'input', 'password', 'long text', 'file', 'number', 'select',
  'list', 'multi select', 'key_value_mapper', 'chip_input', 'readonly',
] as const

/* ------------------------------------------------------- manifest behaviour */

/** Field kinds whose value is never sent — they are display only. */
export function isReadonly(f: ManifestField): boolean {
  return f.input_type === 'readonly'
}

/**
 * Whether `visible_when` is satisfied.
 *
 * The legacy form evaluates this from TWO different sources: the select values
 * at render time, the whole form at submit time. So a `visible_when` pointing
 * at a non-select field hides on screen but is NOT stripped on submit. That is
 * a bug, not a contract, and it is collapsed here to one source — the current
 * form values — deliberately.
 */
export function isVisibleWhen(f: ManifestField, values: ConfigValues): boolean {
  const when = f.visible_when
  if (!when) return true
  const current = values[when.field]
  if (when.value !== undefined) return current === when.value
  if (Array.isArray(when.values)) return when.values.includes(current)
  return true
}

/**
 * Whether a field is on screen, in one predicate.
 *
 * Two mechanisms, both real, and half-implementing either is the bad outcome:
 *
 *   visible_when   declarative, from the manifest
 *   fieldMap       a select whose chosen option NAMES another field reveals
 *                  that field and hides its siblings. This is what switches
 *                  "paste a key" for "upload a key file", so it is entangled
 *                  with the file input type.
 *
 * `mode` is chosen by the CALLER from the plugin's `allow_multi`, not by the
 * manifest. In basic mode `fieldType` gates as well; in advance mode it is
 * ignored entirely.
 */
export function isFieldVisible(
  f: ManifestField,
  fields: readonly ManifestField[],
  values: ConfigValues,
  opts: { mode: 'basic' | 'advance'; showAdvanced?: boolean },
): boolean {
  if (!isVisibleWhen(f, values)) return false
  const revealed = fieldMapReveals(fields, values)
  // A field named by SOME select's fieldMap is shown only when it is the one
  // that select currently points at.
  const display = revealed.has(f.fieldName)
    ? revealed.get(f.fieldName) === true
    : f.DefaultDisplay
  if (opts.mode === 'basic') return f.fieldType === 'Basic' && display
  return display || Boolean(opts.showAdvanced)
}

/**
 * For every field named by any select's `fieldMap`, whether it is currently
 * the revealed one. Fields no fieldMap mentions are absent from the map and
 * fall back to their manifest `DefaultDisplay`.
 */
function fieldMapReveals(
  fields: readonly ManifestField[],
  values: ConfigValues,
): Map<string, boolean> {
  const out = new Map<string, boolean>()
  for (const f of fields) {
    if (f.input_type !== 'select' || !f.fieldMap) continue
    const chosen = values[f.fieldName] ?? f.default
    const target = typeof chosen === 'string' ? f.fieldMap[chosen] : undefined
    for (const name of Object.values(f.fieldMap)) {
      // Later selects do not un-reveal what an earlier one revealed.
      out.set(name, out.get(name) === true || name === target)
    }
  }
  return out
}

/**
 * Whether a field must be filled.
 *
 * A Sensitive field that ALREADY has a saved value is not required: the form
 * shows a mask and "leave blank to keep the current value", and keeping
 * `required` on it would block the save the operator actually intends.
 */
export function isFieldRequired(f: ManifestField, saved: ConfigValues): boolean {
  if (f.Sensitive && Object.prototype.hasOwnProperty.call(saved, f.fieldName)) return false
  return f.required
}

/**
 * Build the payload for save_config.
 *
 * FOUR treatments, and the ORDER of the last two is load-bearing:
 *
 *   1. readonly              stripped — display only
 *   2. visible_when hidden   stripped — the field does not apply
 *   3. never rendered        MERGED BACK from the saved config. Collapsed
 *                            advanced settings are absent from `values`; drop
 *                            this and a save from the basic view silently
 *                            wipes them.
 *   4. Sensitive === mask    cleared to '', which the backend reads as "keep
 *                            the current credential".
 *
 * 4 must run AFTER 3. A Sensitive field that was never rendered is merged back
 * as the mask in step 3; sweeping before that leaves the mask in the payload
 * and stores it AS the credential, and the integration then fails to
 * authenticate with nothing on screen to explain it.
 *
 * `rendered` means "this field was on screen", NOT "the manifest says it
 * should be". A field the operator deliberately cleared is rendered with an
 * empty value, and that empty value must win — merging back on emptiness
 * instead would silently restore what they just deleted.
 */
export function buildConfigPayload(args: {
  fields: readonly ManifestField[]
  /** Values the form produced, keyed by fieldName. */
  values: ConfigValues
  /** The configuration as last saved, from get_config. */
  saved: ConfigValues
  /** Field names that were actually on screen. */
  rendered: readonly string[]
}): ConfigValues {
  const { fields, values, saved, rendered } = args
  const byName = new Map(fields.map((f) => [f.fieldName, f]))
  const renderedSet = new Set(rendered)

  const stripped = new Set(
    fields.filter((f) => isReadonly(f) || !isVisibleWhen(f, values)).map((f) => f.fieldName),
  )

  const out: ConfigValues = {}
  for (const [k, v] of Object.entries(values)) {
    if (!stripped.has(k)) out[k] = v
  }

  // 3 — merge back what was never on screen.
  for (const f of fields) {
    if (renderedSet.has(f.fieldName)) continue
    if (stripped.has(f.fieldName)) continue
    if (!Object.prototype.hasOwnProperty.call(saved, f.fieldName)) continue
    out[f.fieldName] = saved[f.fieldName]
  }

  // 4 — and only now clear the masks, including any just merged back.
  for (const [k, v] of Object.entries(out)) {
    // Exact match, and only on Sensitive fields: a non-sensitive field may
    // legitimately hold this string.
    if (byName.get(k)?.Sensitive && v === SENSITIVE_MASK) out[k] = ''
  }

  return out
}
